import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Select,
  DatePicker,
  Input,
  Tag,
  Tooltip,
  message,
  Space,
  Badge,
  Drawer,
  Divider,
  Typography,
  Row,
  Col,
} from "antd";
import {
  EyeOutlined,
  CheckOutlined,
  CloseOutlined,
  FilterOutlined,
  ClearOutlined,
  SaveOutlined,
  DeleteOutlined,
  EditOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { mockWorkOrders } from "../../data/mockWorkOrders";
import WorkOrderAside from "./workOrderAside";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";
import { Link } from "react-router-dom";

const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

const NewWorkOrders: React.FC = () => {
  const navigate = useNavigate();
  const [sm, setSm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [selectedKPIFilter, setSelectedKPIFilter] = useState<string>("status");

  // Filter states
  const [filters, setFilters] = useState({
    site: "",
    organisation: "",
    status: "",
    contractType: "",
    woOwner: "",
    valueRange: { min: "", max: "" },
    dateRange: null as any,
    globalSearch: "",
  });

  // Drawer states
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [savedFilters, setSavedFilters] = useState<any[]>([]);

  useEffect(() => {
    applyFilters();
  }, [filters, data]);

  const applyFilters = () => {
    let filtered = [...data];

    if (filters.globalSearch) {
      const searchTerm = filters.globalSearch.toLowerCase();
      filtered = filtered.filter(
        (wo: any) =>
          wo.woNumber.toLowerCase().includes(searchTerm) ||
          wo.title.toLowerCase().includes(searchTerm) ||
          wo.contractorName.toLowerCase().includes(searchTerm) ||
          wo.site.toLowerCase().includes(searchTerm) ||
          wo.organisation.toLowerCase().includes(searchTerm)
      );
    }

    if (filters.site) {
      filtered = filtered.filter((wo) => wo.site === filters.site);
    }
    if (filters.organisation) {
      filtered = filtered.filter(
        (wo) => wo.organisation === filters.organisation
      );
    }
    if (filters.status) {
      filtered = filtered.filter((wo) => wo.status === filters.status);
    }
    if (filters.contractType) {
      filtered = filtered.filter(
        (wo) => wo.contractType === filters.contractType
      );
    }
    if (filters.woOwner) {
      filtered = filtered.filter((wo) =>
        wo.contractOwner.toLowerCase().includes(filters.woOwner.toLowerCase())
      );
    }
    if (filters.valueRange.min || filters.valueRange.max) {
      filtered = filtered.filter((wo) => {
        const value = wo.woValue;
        const min = filters.valueRange.min
          ? parseFloat(filters.valueRange.min)
          : 0;
        const max = filters.valueRange.max
          ? parseFloat(filters.valueRange.max)
          : Infinity;
        return value >= min && value <= max;
      });
    }
    if (filters?.dateRange?.length === 2) {
      const [startDate, endDate] = filters.dateRange;
      filtered = filtered.filter((wo) => {
        const releasedDate = dayjs(wo.dateReleased);
        return (
          releasedDate.isAfter(startDate) && releasedDate.isBefore(endDate)
        );
      });
    }

    setFilteredData(filtered);
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      site: "",
      organisation: "",
      status: "",
      contractType: "",
      woOwner: "",
      valueRange: { min: "", max: "" },
      dateRange: null,
      globalSearch: "",
    });
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.site) count++;
    if (filters.organisation) count++;
    if (filters.status) count++;
    if (filters.contractType) count++;
    if (filters.woOwner) count++;
    if (filters.valueRange.min || filters.valueRange.max) count++;
    if (filters.dateRange?.length === 2) count++;
    if (filters.globalSearch) count++;
    return count;
  };

  const saveCurrentFilter = () => {
    const filterName = `Filter ${savedFilters.length + 1}`;
    const newFilter = {
      id: Date.now(),
      name: filterName,
      filters: { ...filters },
      createdAt: new Date().toISOString(),
    };
    setSavedFilters((prev) => [...prev, newFilter]);
    message.success("Filter saved successfully");
  };

  const applySavedFilter = (savedFilter: any) => {
    setFilters(savedFilter.filters);
    message.success(`Applied saved filter: ${savedFilter.name}`);
  };

  const deleteSavedFilter = (filterId: number) => {
    setSavedFilters((prev) => prev.filter((f) => f.id !== filterId));
    message.success("Filter deleted successfully");
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleViewWorkOrder = (record: any) => {
    navigate(`/view-work-order-details/${record.workOrderId}`);
  };

  const handleAccept = (record: any) => {
    navigate(`/view-work-order-details/${record.id}`);
  };

  const handleReject = (record: any) => {
    navigate(`/view-work-order-details/${record.id}`);
  };

  const handleRequestChanges = (record: any) => {
    navigate(`/view-work-order-details/${record.id}`);
  };

  const uniqueSites = [mockWorkOrders.map((wo) => wo.site)];
  const uniqueOrgs = [mockWorkOrders.map((wo) => wo.organisation)];
  const contractTypes = ["Fixed Price", "Time & Material", "Cost Plus"];

  const columns = [
    {
      title: "WO #",
      dataIndex: "workOrderNumber",
      key: "workOrderNumber",
      width: 200,
      render: (text: string, record: any) => (
        <span
          className="inlineTableCellLink"
          id={`woStatus-${record?.workOrderId}`}
          onClick={() => handleViewWorkOrder(record)}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Work Order Title",
      dataIndex: "workOrderTitle",
      key: "workOrderTitle",
      width: 280,
      render: (text: any, record: any) => (
        <div>
          <Link
            to={`/view-work-order-details/${record.workOrderId}`}
            className="fw-bold text-primary"
          >
            {text}
          </Link>
          <div className="text-muted small mt-1">
            {record.siteName}
            {record.projectName && ` • ${record.projectName}`}
          </div>
        </div>
      ),
    },
    {
      title: "Site / Organisation",
      key: "siteName",
      width: 190,
      render: (_: any, record: any) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.siteName}</div>
          <div style={{ fontSize: "12px", color: "#666" }}>
            {record.orgName}
          </div>
        </div>
      ),
    },
    {
      title: "WO Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (date: string) => dayjs(date).format("DD-MMM-YYYY"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 180,
      render: (status: string, record: any) => {
        return <Tag color={"orange"}>{status}</Tag>;
      },
    },
    {
      title: "Last Response",
      dataIndex: "dateReleased",
      key: "dateReleased",
      width: 120,
      render: (date: string) => dayjs(date).format("DD-MMM-YYYY"),
    },
    {
      title: "SLA Age (Days)",
      key: "slaAgingForConfirmation",
      width: 150,
      render: (_: any, record: any) => {
        const age = record.slaAgingForConfirmation;
        return (
          <span
            style={{
              color: age > 2 ? "#ff4d4f" : age > 1 ? "#faad14" : "#52c41a",
              fontWeight: 500,
            }}
          >
            {age}
          </span>
        );
      },
    },
    // {
    //   title: "Actions",
    //   key: "actions",
    //   width: 200,
    //   fixed: "right" as const,
    //   render: (_: any, record: any) => (
    //     <Space>
    //       <Tooltip title="View Details">
    //         <Button
    //           type="primary"
    //           ghost
    //           size="small"
    //           icon={<EyeOutlined />}
    //           onClick={() => handleViewWorkOrder(record)}
    //         />
    //       </Tooltip>
    //       <Tooltip title="Accept">
    //         <Button
    //           type="primary"
    //           size="small"
    //           icon={<CheckOutlined />}
    //           onClick={() => handleAccept(record)}
    //           style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
    //         />
    //       </Tooltip>
    //       <Tooltip title="Request Changes">
    //         <Button
    //           size="small"
    //           icon={<EditOutlined />}
    //           onClick={() => handleRequestChanges(record)}
    //         />
    //       </Tooltip>
    //       <Tooltip title="Reject">
    //         <Button
    //           danger
    //           size="small"
    //           icon={<CloseOutlined />}
    //           onClick={() => handleReject(record)}
    //         />
    //       </Tooltip>
    //     </Space>
    //   ),
    // },
  ];

  return (
    <>
      <TransactionFlowDefaultTemplate
        moduleKey="new-work-orders"
        title="New Work Orders"
        columns={columns}
        sideNavigation={
          <div
            className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
              sm ? "content-active" : ""
            }`}
          >
            <WorkOrderAside updateSm={setSm} sm={sm} />
          </div>
        }
        extra={{
          tableScrollX: 1500,
          dateRange: false,
          excelExport: true,
          dataFilterTootip: "Filtering based on Work Order released date",
        }}
        topRightJSX={
          <Space>
            <Badge count={getActiveFilterCount()} size="small">
              <Button
                type="primary"
                icon={<FilterOutlined />}
                onClick={() => setFilterDrawerVisible(true)}
              >
                Advanced Filters
              </Button>
            </Badge>
          </Space>
        }
      />

      {/* Advanced Filter Drawer */}
      <Drawer
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>
              <FilterOutlined style={{ marginRight: 8 }} />
              Filters
            </span>
            <Badge count={getActiveFilterCount()} showZero={false} />
          </div>
        }
        placement="right"
        width={420}
        open={filterDrawerVisible}
        onClose={() => setFilterDrawerVisible(false)}
        extra={
          <Space>
            <Button
              icon={<SaveOutlined />}
              onClick={saveCurrentFilter}
              disabled={getActiveFilterCount() === 0}
            >
              Save Filter
            </Button>
            <Button
              icon={<ClearOutlined />}
              onClick={clearFilters}
              disabled={getActiveFilterCount() === 0}
            >
              Reset
            </Button>
          </Space>
        }
      >
        <div style={{ padding: "0 0 16px 0" }}>
          {/* Saved Filters */}
          {savedFilters.length > 0 && (
            <>
              <div style={{ marginBottom: 24 }}>
                <Title level={5} style={{ marginBottom: 12 }}>
                  Saved Filters
                </Title>
                <div style={{ maxHeight: 120, overflowY: "auto" }}>
                  {savedFilters.map((savedFilter) => (
                    <div
                      key={savedFilter.id}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "4px 0",
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      <Button
                        type="link"
                        size="small"
                        onClick={() => applySavedFilter(savedFilter)}
                        style={{
                          padding: 0,
                          height: "auto",
                          textAlign: "left",
                        }}
                      >
                        {savedFilter.name}
                      </Button>
                      <Button
                        type="text"
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => deleteSavedFilter(savedFilter.id)}
                        danger
                      />
                    </div>
                  ))}
                </div>
              </div>
              <Divider />
            </>
          )}

          {/* Filter Fields */}
          <div className="filter-section">
            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: "block", marginBottom: 6 }}>
                Site
              </Text>
              <Select
                placeholder="Select Site"
                value={filters.site}
                onChange={(value) => handleFilterChange("site", value)}
                style={{ width: "100%" }}
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {uniqueSites.map((site: any) => (
                  <Option key={site} value={site}>
                    {site}
                  </Option>
                ))}
              </Select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: "block", marginBottom: 6 }}>
                Organisation
              </Text>
              <Select
                placeholder="Select Organisation"
                value={filters.organisation}
                onChange={(value) => handleFilterChange("organisation", value)}
                style={{ width: "100%" }}
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {uniqueOrgs.map((org: any) => (
                  <Option key={org} value={org}>
                    {org}
                  </Option>
                ))}
              </Select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: "block", marginBottom: 6 }}>
                Contract Type
              </Text>
              <Select
                placeholder="Select Contract Type"
                value={filters.contractType}
                onChange={(value) => handleFilterChange("contractType", value)}
                style={{ width: "100%" }}
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {contractTypes.map((type) => (
                  <Option key={type} value={type}>
                    {type}
                  </Option>
                ))}
              </Select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: "block", marginBottom: 6 }}>
                Contract Owner
              </Text>
              <Input
                placeholder="Enter Contract Owner name"
                value={filters.woOwner}
                onChange={(e) => handleFilterChange("woOwner", e.target.value)}
                allowClear
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: "block", marginBottom: 6 }}>
                Date Range
              </Text>
              <RangePicker
                value={filters.dateRange}
                onChange={(dates) => handleFilterChange("dateRange", dates)}
                style={{ width: "100%" }}
                placeholder={["Start Date", "End Date"]}
                format="DD/MM/YYYY"
              />
            </div>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default NewWorkOrders;
